import { 
  LTSSService, 
  Alert, 
  AlertLevel, 
  OVERNIGHT_ALLOWED_PROGRAMS 
} from '../types/ltss';
import { extractHour, getMonthKey } from './timeUtils';

// Simple UUID generator (no external dependency)
function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

export function generateAlerts(services: LTSSService[]): Alert[] {
  const alerts: Alert[] = [];

  // 1. Missing Time Alerts (CRITICAL)
  services
    .filter(s => s.serviceStatus === 'New: Missing Time')
    .forEach(service => {
      // Determine if it's missing clock-in or clock-out
      const hasStartTime = service.startTime && service.startTime.trim() !== '';
      const hasEndTime = service.endTime && service.endTime.trim() !== '';
      
      let missingType = 'clock-in/clock-out';
      if (hasStartTime && !hasEndTime) {
        missingType = 'clock-out';
      } else if (!hasStartTime && hasEndTime) {
        missingType = 'clock-in';
      }
      
      alerts.push({
        id: generateId(),
        type: 'CRITICAL',
        category: 'Missing Time',
        message: `Missing ${missingType} for ${service.clientName}`,
        details: `Date: ${service.dayOfService || service.serviceDate?.toLocaleDateString() || 'Unknown'}${hasStartTime ? `, Start: ${service.startTime}` : ''}${hasEndTime ? `, End: ${service.endTime}` : ''}`,
        relatedService: service,
        staffName: service.staffName,
        clientName: service.clientName,
        timestamp: service.serviceDate || undefined,
      });
    });

  // 2. Not Authorized Alerts (CRITICAL)
  services
    .filter(s => s.serviceStatus === 'Not Authorized')
    .forEach(service => {
      alerts.push({
        id: generateId(),
        type: 'CRITICAL',
        category: 'Not Authorized',
        message: `Service for ${service.clientName} is NOT AUTHORIZED`,
        details: `Staff: ${service.staffName}, Program: ${service.program}`,
        relatedService: service,
        staffName: service.staffName,
        clientName: service.clientName,
        timestamp: service.serviceDate || undefined,
      });
    });

  // 3. Penalty Point Tracking (per staff per month)
  const staffMonthlyManual: Record<string, { count: number; staffName: string; month: string }> = {};
  
  services.forEach(service => {
    if (service.serviceInitiationSource?.includes('Provider Manual')) {
      const month = getMonthKey(service.serviceDate, service.startTime);
      const key = `${service.staffName}|${month}`;
      
      if (!staffMonthlyManual[key]) {
        staffMonthlyManual[key] = { count: 0, staffName: service.staffName, month };
      }
      staffMonthlyManual[key].count++;
    }
  });

  Object.values(staffMonthlyManual).forEach(record => {
    if (record.count >= 4) {
      alerts.push({
        id: generateId(),
        type: 'CRITICAL',
        category: 'Penalty Exceeded',
        message: `${record.staffName} has ${record.count} manual corrections in ${record.month}`,
        details: 'MDH will REJECT claims - exceeded 4-point monthly limit',
        staffName: record.staffName,
      });
    } else if (record.count === 3) {
      alerts.push({
        id: generateId(),
        type: 'WARNING',
        category: 'Penalty Risk',
        message: `${record.staffName} has ${record.count}/4 manual corrections in ${record.month}`,
        details: 'One more manual correction will trigger claim rejections',
        staffName: record.staffName,
      });
    }
  });

  // 4. Suspicious Overnight (non-REM/EPSDT)
  services.forEach(service => {
    if (!OVERNIGHT_ALLOWED_PROGRAMS.includes(service.program)) {
      const hour = extractHour(service.startTime);
      if (hour !== null && (hour >= 22 || hour < 5)) {
        alerts.push({
          id: generateId(),
          type: 'WARNING',
          category: 'Suspicious Overnight',
          message: `Clocked in at unusual hour for ${service.program} client`,
          details: `${service.clientName} - Start: ${service.startTime}`,
          relatedService: service,
          staffName: service.staffName,
          clientName: service.clientName,
          timestamp: service.serviceDate || undefined,
        });
      }
    }
  });

  // 5. Hour Overage (per client per week)
  const clientWeekHours: Record<string, {
    clientName: string;
    week: string;
    authorized: number;
    used: number;
  }> = {};

  services.forEach(service => {
    const key = `${service.clientName}|${service.week}`;
    
    if (!clientWeekHours[key]) {
      clientWeekHours[key] = {
        clientName: service.clientName,
        week: service.week,
        authorized: service.weeklyPosHours || 0,
        used: 0,
      };
    }
    clientWeekHours[key].used += service.serviceLength || 0;
  });

  Object.values(clientWeekHours).forEach(record => {
    if (record.authorized > 0 && record.used > record.authorized) {
      const overage = record.used - record.authorized;
      alerts.push({
        id: generateId(),
        type: 'CAUTION',
        category: 'Hour Overage',
        message: `${record.clientName} exceeded weekly authorization`,
        details: `Used ${record.used.toFixed(1)} hrs vs ${record.authorized} authorized (+${overage.toFixed(1)} hrs overage)`,
        clientName: record.clientName,
      });
    }
  });

  // 6. Pending MDH (INFO)
  services
    .filter(s => s.serviceStatus === 'Pending MDH')
    .forEach(service => {
      alerts.push({
        id: generateId(),
        type: 'INFO',
        category: 'Pending Review',
        message: `Service for ${service.clientName} pending MDH review`,
        details: `Staff: ${service.staffName}`,
        relatedService: service,
        staffName: service.staffName,
        clientName: service.clientName,
        timestamp: service.serviceDate || undefined,
      });
    });

  // 7. Overlapping Services Detection (CRITICAL)
  // Group services by staff and date to check for overlaps
  // Include services with missing end times - those cause overlaps!
  const staffDateServices: Record<string, LTSSService[]> = {};
  
  services.forEach(service => {
    // Include if has start time (even if missing end time - that's an overlap risk)
    if (service.staffName && service.serviceDate && service.startTime) {
      const dateStr = new Date(service.serviceDate).toDateString();
      const key = `${service.staffName}|${dateStr}`;
      if (!staffDateServices[key]) {
        staffDateServices[key] = [];
      }
      staffDateServices[key].push(service);
    }
  });

  // Helper to parse time string to minutes
  const parseTime = (timeStr: string | null): number | null => {
    if (!timeStr) return null;
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!match) return null;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3]?.toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  // Check each staff's daily services for overlaps
  const reportedOverlaps = new Set<string>(); // Avoid duplicate alerts
  
  Object.entries(staffDateServices).forEach(([, dailyServices]) => {
    if (dailyServices.length < 2) return;
    
    // Parse times and sort by start time
    const parsedServices = dailyServices.map(s => ({
      service: s,
      start: parseTime(s.startTime),
      end: parseTime(s.endTime), // Can be null for missing clock-out
    })).filter(p => p.start !== null);

    // Sort by start time
    parsedServices.sort((a, b) => (a.start || 0) - (b.start || 0));

    // Check for overlaps
    for (let i = 0; i < parsedServices.length - 1; i++) {
      const current = parsedServices[i];
      const next = parsedServices[i + 1];
      
      // Case 1: Same start time = overlap (duplicate clock-in)
      // This happens when staff clocks in twice at the same time
      if (current.start === next.start) {
        const overlapKey = `${current.service.staffName}|${current.service.serviceDate}|${current.service.clientName}|${next.service.clientName}|same`;
        
        if (!reportedOverlaps.has(overlapKey)) {
          reportedOverlaps.add(overlapKey);
          
          alerts.push({
            id: generateId(),
            type: 'CRITICAL',
            category: 'Overlapping Services',
            message: `Duplicate clock-in at same time`,
            details: `Both ${current.service.clientName} and ${next.service.clientName} started at ${current.service.startTime}`,
            relatedService: current.service,
            staffName: current.service.staffName,
            clientName: current.service.clientName,
            timestamp: current.service.serviceDate || undefined,
          });
        }
      }
      // Case 2: Both have times but they overlap (next starts before current ends)
      else if (current.end !== null && next.start !== null && next.start < current.end) {
        const overlapKey = `${current.service.staffName}|${current.service.serviceDate}|${current.service.clientName}|${next.service.clientName}|time`;
        
        if (!reportedOverlaps.has(overlapKey)) {
          reportedOverlaps.add(overlapKey);
          
          alerts.push({
            id: generateId(),
            type: 'CRITICAL',
            category: 'Overlapping Services',
            message: `Overlapping services`,
            details: `${current.service.clientName} (${current.service.startTime}-${current.service.endTime}) overlaps with ${next.service.clientName} (${next.service.startTime}-${next.service.endTime})`,
            relatedService: current.service,
            staffName: current.service.staffName,
            clientName: current.service.clientName,
            timestamp: current.service.serviceDate || undefined,
          });
        }
      }
    }
  });

  // 8. Multiple Staff Serving Same Client at Same Time (CRITICAL)
  // Group services by client and date to check for multiple staff overlaps
  const clientDateServices: Record<string, LTSSService[]> = {};
  
  services.forEach(service => {
    if (service.clientName && service.serviceDate && service.startTime) {
      const dateStr = new Date(service.serviceDate).toDateString();
      const key = `${service.clientName}|${dateStr}`;
      if (!clientDateServices[key]) {
        clientDateServices[key] = [];
      }
      clientDateServices[key].push(service);
    }
  });

  const reportedClientOverlaps = new Set<string>();
  
  Object.entries(clientDateServices).forEach(([, dailyServices]) => {
    // Only check if multiple different staff served this client on same day
    const uniqueStaff = new Set(dailyServices.map(s => s.staffName));
    if (uniqueStaff.size < 2) return;
    
    // Parse times
    const parsedServices = dailyServices.map(s => ({
      service: s,
      start: parseTime(s.startTime),
      end: parseTime(s.endTime),
    })).filter(p => p.start !== null);

    // Check each pair of services from different staff
    for (let i = 0; i < parsedServices.length; i++) {
      for (let j = i + 1; j < parsedServices.length; j++) {
        const svc1 = parsedServices[i];
        const svc2 = parsedServices[j];
        
        // Skip if same staff
        if (svc1.service.staffName === svc2.service.staffName) continue;
        
        // Check if times overlap
        const start1 = svc1.start!;
        const end1 = svc1.end ?? 1440; // If no end, assume end of day
        const start2 = svc2.start!;
        const end2 = svc2.end ?? 1440;
        
        // Overlap if one starts before the other ends
        const overlaps = (start1 < end2 && start2 < end1);
        
        if (overlaps) {
          const overlapKey = `${svc1.service.clientName}|${svc1.service.serviceDate}|${svc1.service.staffName}|${svc2.service.staffName}`;
          const reverseKey = `${svc1.service.clientName}|${svc1.service.serviceDate}|${svc2.service.staffName}|${svc1.service.staffName}`;
          
          if (!reportedClientOverlaps.has(overlapKey) && !reportedClientOverlaps.has(reverseKey)) {
            reportedClientOverlaps.add(overlapKey);
            
            alerts.push({
              id: generateId(),
              type: 'CRITICAL',
              category: 'Multiple Staff Overlap',
              message: `Multiple staff serving ${svc1.service.clientName} at same time`,
              details: `${svc1.service.staffName} (${svc1.service.startTime}-${svc1.service.endTime || 'no clock-out'}) and ${svc2.service.staffName} (${svc2.service.startTime}-${svc2.service.endTime || 'no clock-out'})`,
              clientName: svc1.service.clientName,
            });
          }
        }
      }
    }
  });

  // Sort by severity: CRITICAL > WARNING > CAUTION > INFO
  const severityOrder: Record<AlertLevel, number> = {
    CRITICAL: 0,
    WARNING: 1,
    CAUTION: 2,
    INFO: 3,
  };

  return alerts.sort((a, b) => severityOrder[a.type] - severityOrder[b.type]);
}
